import { alpha, type Theme } from '@mui/material/styles';

/**
 * Styles for the customization pages.
 *
 * Colours come from the theme (`divider`, `action.hover`, `background.paper`) rather than
 * from literals, so this page cannot drift away from the rest of the app the way a
 * hand-picked hex would. The pills and icon tiles are the same ones `Teams.styles.ts`
 * uses; only the field-type accents in `fieldTypes.tsx` are their own thing, and those
 * are semantic — they identify an input type, they are not chrome.
 */

export const countPillStyles = {
  minWidth: 24,
  px: 1,
  lineHeight: '20px',
  textAlign: 'center',
  borderRadius: '999px',
  backgroundColor: '#26272F',
  border: '1px solid rgb(54, 56, 67)',
  color: 'text.secondary',
  fontSize: 12,
  fontWeight: 600,
};

/** Leading tile on a catalogue row, the same one the team and folder lists use. */
export const typeIconStyles = {
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: '.5rem',
  backgroundColor: '#26272F',
  border: '1px solid rgb(54, 56, 67)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#B5B7C8',
  fontSize: 20,
};

export const detailIconStyles = {
  ...typeIconStyles,
  width: 52,
  height: 52,
  borderRadius: '.6rem',
  fontSize: 26,
};

/** Destructive action in a header row: neutral until hovered, like the teams pages. */
export const dangerHoverStyles = {
  color: 'text.secondary',
  '&:hover': { color: 'error.light' },
};

export const backButtonStyles = {
  mb: 2,
  px: 1,
  color: 'text.secondary',
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': { color: 'text.primary', backgroundColor: 'transparent' },
};

/** One POI type's row. Same rhythm and hover as a team row. */
export const typeRowStyles = {
  px: 2.25,
  py: 2,
  gap: 2,
  cursor: 'pointer',
  userSelect: 'none',
  alignItems: 'center',
  transition: 'background-color 120ms ease',
  '&:hover': { backgroundColor: 'action.hover' },
};

/** The expanded half of a type, set back from the header by a hair. */
export const typeBodyStyles = {
  borderTop: '1px solid',
  borderColor: 'divider',
  backgroundColor: (theme: Theme) => alpha(theme.palette.common.black, 0.16),
};

export const groupStyles = {
  borderBottom: '1px solid',
  borderColor: 'divider',
  '&:last-of-type': { borderBottom: 0 },
};

export const groupHeaderStyles = {
  px: 2.25,
  py: 1.25,
  gap: 1.25,
  alignItems: 'center',
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'background-color 120ms ease',
  '&:hover': { backgroundColor: 'action.hover' },
};

/**
 * Inherited content is drawn as one tinted block, so "these fields are not yours to edit"
 * reads before any individual lock icon does.
 */
export const inheritedStyles = {
  backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.07),
  '& .MuiTableRow-root:hover': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.1),
  },
};

/**
 * Header over everything one ancestor contributes.
 *
 * Naming the source once for the whole region is what keeps it off each group header and
 * out of the field table: a chip per block repeated the same parent name down the page and
 * cost the table an extra row of height.
 */
export const inheritedRegionHeaderStyles = {
  px: 2.25,
  py: 0.75,
  gap: 1,
  alignItems: 'center',
  borderBottom: '1px solid',
  borderColor: 'divider',
  backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.11),
};

/**
 * The preview's screen. Narrow and bordered so the form reads as something rendered on a
 * device rather than as more of the editor around it — the whole point of the preview is
 * that the two look different.
 */
export const previewFrameStyles = {
  maxWidth: 400,
  mx: 'auto',
  p: 2.25,
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.default',
};

/**
 * A group in the preview is a heading and nothing else — no card, no tint, no indent — so
 * its fields line up with the ungrouped ones exactly. Being collapsible is what marks the
 * grouping; drawing a box around it as well nested one frame inside another for no gain.
 */
export const previewGroupHeaderStyles = {
  gap: 1,
  alignItems: 'center',
  cursor: 'pointer',
  userSelect: 'none',
  py: 0.25,
};

export const inheritBannerStyles = {
  px: 2.25,
  py: 1.25,
  gap: 1,
  alignItems: 'center',
  borderBottom: '1px solid',
  borderColor: 'divider',
  backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.09),
  color: 'primary.light',
};

/**
 * Field rows share the 18px left edge of the type and group headers; the name column then
 * starts a further 34px in, which is the width the prototype gives its drag handle. That
 * offset is what makes a field read as sitting *under* its group rather than beside it.
 */
export const fieldTableStyles = {
  tableLayout: 'fixed',
  '& td': {
    borderBottom: '1px solid',
    borderColor: 'divider',
    py: 1.25,
  },
  '& td:first-of-type': { pl: 6.5 },
  '& td:last-of-type': { pr: 2.25 },
  '& tr:last-of-type td': { borderBottom: 0 },
};

export const requiredChipStyles = {
  height: 20,
  fontSize: 11,
  fontWeight: 600,
  '& .MuiChip-label': { px: 0.9 },
};

export const dashedButtonStyles = {
  color: 'text.secondary',
  border: '1px dashed',
  borderColor: 'divider',
  '&:hover': {
    borderStyle: 'dashed',
    borderColor: 'primary.main',
    color: 'primary.light',
    backgroundColor: 'transparent',
  },
};

/**
 * A switch presented as its own bordered row: what it does on the left, the control on
 * the right. The prototype does the same, and the reason holds here — these options need
 * a sentence of explanation ("the inspector must fill this before saving"), which a bare
 * checkbox label has nowhere to put.
 */
export const toggleRowStyles = {
  px: 1.75,
  py: 1.5,
  gap: 2,
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
};

/**
 * A Range in the preview: the row a Toggle already uses, turned to a column so the slider
 * gets the full width.
 *
 * Sharing `toggleRowStyles` is the point — these are the two controls MUI draws no border
 * for, and without the shared frame they read as loose next to the outlined text inputs.
 */
export const previewRangeStyles = {
  ...toggleRowStyles,
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 0.5,
};

/** Field-type picker: a grid of cards, one per input type. */
export const typeGridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: 1.25,
};

export const typeCardOptionStyles = {
  px: 1,
  py: 1.5,
  gap: 1,
  alignItems: 'center',
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  cursor: 'pointer',
  textAlign: 'center',
  transition: 'background-color 120ms ease, border-color 120ms ease',
  '&:hover': { backgroundColor: 'action.hover' },
};

export const typeCardOptionSelectedStyles = {
  borderColor: 'primary.main',
  backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.12),
};

/** Numbered section header inside a drawer, mirroring the prototype's stepped layout. */
export const stepLabelStyles = {
  gap: 1.25,
  alignItems: 'center',
  mb: 1.5,
};

export const stepNumberStyles = {
  width: 20,
  height: 20,
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  fontSize: 11,
  fontWeight: 700,
  backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.16),
  color: 'primary.light',
};

/**
 * Drawer chrome, on the same rhythm as the dialogs (`Teams.styles.ts`) so the two read as
 * one family: 2.5 in from the edge, a divider under the header and above the footer.
 *
 * `backgroundImage: 'none'` is the load-bearing line. MUI paints a lightening elevation
 * overlay on paper in dark mode, which leaves a drawer visibly paler than the page it
 * sits against; the dialogs already switch it off for the same reason.
 */
export const drawerPaperStyles = {
  backgroundImage: 'none',
  borderLeft: '1px solid',
  borderColor: 'divider',
};

/** Same treatment as the drawer and dialog papers — see `drawerPaperStyles` for why. */
export const popoverPaperStyles = {
  backgroundImage: 'none',
  border: '1px solid',
  borderColor: 'divider',
  width: 340,
  maxWidth: '92vw',
};

export const drawerHeaderStyles = {
  px: 2.5,
  pt: 2.5,
  pb: 2,
};

export const drawerBodyStyles = {
  px: 2.5,
  py: 2.5,
};

export const drawerFooterStyles = {
  px: 2.5,
  py: 1.75,
};

/** Buttons in this section keep sentence case, like the rest of the app's dialogs. */
export const buttonStyles = {
  textTransform: 'none',
  fontWeight: 600,
};

export const drawerCloseStyles = {
  color: 'text.secondary',
  mt: '-2px',
  mr: '-4px',
};
